import { Component, OnInit, OnDestroy, AfterViewInit, HostListener, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import isEmpty from 'lodash-es/isEmpty';

import { FinderAnalyticsService } from '@finder/shared/services/finder-analytics/finder-analytics.service';
import { DebugPanelService } from '@finder/shared/services/debug-panel/debug-panel.service';
import { UrlParamsService } from '@finder/shared/services/url-params/url-params.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { FooterService } from '@finder/shared/services/footer-service/footer-service.service';
import { FooterConfig } from '@finder/shared/services/footer-service/footer.class';
import { WebLinkValidationService } from '@finder/shared/services/web-link-validation/web-link-validation.service';
import { InterstitialService } from '@finder/shared/services/interstitial-service/interstitial-service.service';
import { InterstitialModal } from '@finder/shared/services/interstitial-service/interstitial-modal.interface';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { CommonModule } from '@angular/common';
import { ParallaxDirective } from '@finder/shared/directives/parallax/parallax.directive';
import { CurrencyConverterWidgetComponent } from '@finder/shared/components/currency-converter/widget/currency-converter.widget.component';
import { CampaignCountdownClockComponent } from './components/campaign-countdown-clock/campaign-countdown-clock.component';
import { HeroStoryComponent } from './components/hero-story/hero-story.component';
import { FlipClockComponent } from './components/flip-clock/flip-clock.component';
import { InterstitialTriggerDirective } from '@finder/shared/directives/interstitial-trigger/interstitial-trigger.directive';
import { BgImageDirective } from '@finder/shared/directives/bg-image/bg-image.directive';
import { IconBarComponent } from './components/icon-bar/icon-bar.component';
import { OverviewCtaComponent } from './components/overview-cta/overview-cta.component';
import { OverviewAagComponent } from './components/overview-aag/overview-aag.component';
import { FontIconComponent } from '@finder/shared/components/font-icon/font-icon.component';
import { AnchorComponent } from '@finder/shared/components/anchor/anchor.component';
import { ButtonComponent } from '@finder/shared/components/button/button.component';
import { ContentHeaderComponent } from '@finder/shared/components/content-header/content-header.component';
import { CmsMarginDirective } from '@finder/shared/directives/cms-margin/cms-margin.directive';
import { InfoBarComponent } from './components/info-bar/info-bar.component';
import { OverviewRefurbMsgComponent } from './components/overview-refurb-msg/overview-refurb-msg.component';
import { ContentNavigationComponent } from '@finder/shared/components/content-navigation/content-navigation.component';
import { CampaignStickyCtaComponent } from '@finder/shared/components/campaign-sticky-cta/campaign-sticky-cta.component';
import { ImageDrawerComponent } from '@finder/shared/components/image-drawer/image-drawer.component';
import { BlockLayoutRendererComponent } from '@finder/shared/components/block-layout-renderer/block-layout-renderer.component';
import { DisclaimerComponent } from '@finder/shared/components/disclaimer/disclaimer.component';
import { LoadingService } from '@finder/shared/services/loading-service/loading-service.service';
import { ParallaxBackgroundService } from '@finder/shared/components/parallax-background/parallax-background.service';
import { CalendarService } from '@finder/shared/services/calendar-service/calendar-service.service';
import { GetSimpleDatePipe } from '@finder/shared/components/calendar/get-simple-date.pipe';
import { IdGeneratorPipe } from '@finder/shared/utils/pipes/id-generator/id-generator.pipe';

@Component({
    selector: 'app-campaign',
    templateUrl: './campaign.component.html',
    styleUrls: ['./campaign.component.scss'],
    standalone: true,
    imports: [
        BgImageDirective,
        BlockLayoutRendererComponent,
        ButtonComponent,
        CampaignCountdownClockComponent,
        CampaignStickyCtaComponent,
        CmsMarginDirective, // finderCmsMargin
        CommonModule,
        ContentHeaderComponent,
        ContentNavigationComponent,
        CurrencyConverterWidgetComponent,
        DisclaimerComponent,
        FlipClockComponent,
        FontIconComponent,
        HeroStoryComponent,
        IconBarComponent,
        ImageDrawerComponent,
        InfoBarComponent,
        InterstitialTriggerDirective, // finderInterstitialTrigger
        OverviewAagComponent,
        OverviewCtaComponent,
        OverviewRefurbMsgComponent,
        ParallaxDirective, // finderParallax
    ],
    providers: [
        WindowRef,
        FooterService,
        WebLinkValidationService,
        // These were added so the `theme-engine` page could work. None of the services are a singleton.
        ParallaxBackgroundService,
        CalendarService,
        GetSimpleDatePipe,
        IdGeneratorPipe,
    ]
})
export class CampaignComponent implements OnInit, OnDestroy, AfterViewInit {
    svcResponse: any;
    contentNavigation: any;
    imageDrawer: any;
    enableHorizontalRule = false;
    contentHeaderData: false;
    emptyInfoBar = false;
    bottomClock = false;
    isMobile: boolean;

    private destroy$ = new Subject<void>();
    protected interstitialModal: InterstitialModal;

    constructor(
        private route: ActivatedRoute,
        private finderAnalyticsService: FinderAnalyticsService,
        private debugPanelService: DebugPanelService,
        private viewContainerRef: ViewContainerRef,
        private logger: LoggerService,
        private urlParamsService: UrlParamsService,
        private windowRef: WindowRef,
        private footerService: FooterService,
        private webLinkValidationService: WebLinkValidationService,
        private interstitialService: InterstitialService,
        private viewportService: ViewportDetectionService,
        private loadingService: LoadingService,
    ) {}

    ngOnInit() {
        this.isMobile = this.viewportService.isMobile();
        this.route.paramMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                this.urlParamsService.setParams(params);
            });

        const snapshotData = get(this.route.snapshot.data, 'svc');

        if (snapshotData) {
            this.processData(snapshotData);
        }

        this.emptyInfoBar = !this.svcResponse.infoBarData && !this.svcResponse.iconBarData;
        this.bottomClock = get(this.svcResponse.countdownClock, 'position', '').includes('bottom');
        this.setupInterstitialData();

        // Let the loader know the page is ready
        this.loadingService.setLoadingState(true);
    }

    ngAfterViewInit() {
        this.viewportService.onViewportResize()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isMobile = this.viewportService.isMobile();
            });

        this.debugPanelService.createDebugPanel(this.viewContainerRef, this.svcResponse);
        if (this.svcResponse && this.svcResponse.footerConfig) {
            this.footerService.emitFooter(new FooterConfig(this.svcResponse.footerConfig.hideFooterSeparator));
        }

        // GIT-34532 support for text on custom pages
        if (has(this.svcResponse, 'overviewPageType')) {
            // Add the overview type so we can create css classes to properly style the terms pages
            this.windowRef.nativeWindow.document.body.classList.add(this.svcResponse.overviewPageType);
        }
    }

    ngOnDestroy() {
        // Ignore the if just for coverage, there's no need to test unsubscribe
        /* istanbul ignore next */
        this.destroy$.next();
        this.destroy$.complete();
        // Restore state of horizontal separator
        if (this.svcResponse && this.svcResponse.footerConfig) {
            this.footerService.restoreDefault();
        }
    }

    checkContentNavigation() {
        this.contentNavigation  = get(this.svcResponse, 'contentNavigation');
    }

    checkImageDrawer() {
        this.imageDrawer = get(this.svcResponse, 'imageDrawer');
    }

    print() {
        this.windowRef.nativeWindow.print();
    }

    removeFlipClockModule(){
        this.svcResponse.flipClock = false;
    }

    private processData(data) {
        this.webLinkValidationService.validateWebLink(data.webLink);
        this.finderAnalyticsService.initAnalyticsLibrary(data.analytics);
        this.finderAnalyticsService.getGuestModel()
            .pipe(takeUntil(this.destroy$))
            .subscribe(guestModel => {
                const headerTitle = get(data.headerData, 'title', '');

                if (isEmpty(headerTitle)) {
                    this.logger.warn(`CampaignComponent: no header title is set for ${this.windowRef.nativeWindow.location.href}`);
                }
                // Update the analytics model
                this.finderAnalyticsService.updateAnalyticsModel(
                    this.urlParamsService.params,
                    guestModel,
                    data.id,
                    headerTitle);
            });

        // Set data
        this.svcResponse = data;

        // Check that we have header content data
        const headerData = this.svcResponse.headerData;
        this.contentHeaderData = headerData?.title || headerData?.subtitle || headerData?.text || false;

        this.enableHorizontalRule = headerData && headerData?.disableHorizontalRule !== 'true' && this.contentHeaderData;

        this.checkContentNavigation();
        this.checkImageDrawer();
    }

    private setupInterstitialData() {
        this.interstitialService.getInterstitialData()
        .pipe(takeUntil(this.destroy$))
            .subscribe(interstitialData => {
                this.interstitialModal = interstitialData;
            });
    }
}
